import { ICoreComponent } from '../../ICoreComponent';

/**
 * The Countdown Timer displays a stylized segmented timer.
 */
export interface ICountdown extends ICoreComponent {
  /**
   * The selector for this component.
   *
   * This is required.
   */
  selector: 'app-countdown';

  /**
   * What is the format of the countdown?
   *
   * It can be:
   *
   *   - days
   *   - hours
   *   - minutes
   *   - seconds
   *
   * This is required.
   */
  format: CountdownFormat;

  /**
   * What are the labels of the units of time?
   *
   * For example:
   *
   * ____________________________________________
   * |          |          |          |         |
   * |    02    |    02    |    02    |   02    |
   * |          |          |          |         |
   * --------------------------------------------
   * |   Days   |  Hours   | Minutes  | Seconds |
   * --------------------------------------------
   *
   * The labels have singular and plural forms. "Day" and "Days" for example.
   *
   * Labels are optional but if you provide them, you must provide all of them.
   *
   * The default is undefined.
   *
   * This is optional.
   */
  labels?: ICountdownLabels;

  /**
   * The server's local Unix Time (seconds) at the moment the response was
   * produced. The client anchors the countdown to this value — remaining time
   * is `endTime` minus (`startTime` plus the client time elapsed since the
   * config arrived) — so a skewed client clock cannot distort the countdown.
   *
   * This is required.
   */
  startTime: number;

  /**
   * This is the Unix Time as an integer of the target time. This is the value where
   * the timer will end and stop counting down.
   *
   * This is required.
   */
  endTime: number;

  /**
   * Once the countdown reaches zero, this endpoint is called exactly once.
   * The response provides a `destination` link (required) and an optional
   * `destinationDelay` in milliseconds; after the delay, the client navigates
   * to the destination.
   *
   * This is optional — without it, the countdown is for display purposes only
   * and simply stops at zero.
   *
   * @request ICountdownRequestData
   * @response ICountdownResponseData
   */
  onCompleteAPI?: string;
}

/**
 * Used with {@link ICountdown} to specify the format of the countdown.
 */
export enum CountdownFormat {
  DAYS = 'days',
  HOURS = 'hours',
  MINUTES = 'minutes',
  SECONDS = 'seconds',
}

/**
 * Used with {@link ICountdown} to specify translation for labels.
 */
export interface ICountdownLabels {
  /**
   * The singular translation for "day".
   */
  day: string;

  /**
   * The translation for "days".
   */
  days: string;

  /**
   * The singular translation for "hour".
   */
  hour: string;

  /**
   * The translation for "hours".
   */
  hours: string;

  /**
   * The singular translation for "minute".
   */
  minute: string;

  /**
   * The translation for "minutes".
   */
  minutes: string;

  /**
   * The singular translation for "second".
   */
  second: string;

  /**
   * The translation for "seconds".
   */
  seconds: string;
}
